import { type Car } from '@prisma/client'
import {
  calculateDiscountedPrice,
  calculateFinancedAmount,
  formatPrice,
  getApplicableTIN,
  openingCommission,
} from '#app/shared/lib/price.ts'
import { Spacer } from '#app/shared/ui/data-display/Spacer.js'
import { Button } from '#app/shared/ui/inputs/button/Button.js'
import {
  type WarrantyDurationTypes,
  type WarrantyTypes,
  WarrantyTranslationEnum,
} from '../../../../../../types/enums'
import CardOptions from './cardOptions'

type Props = {
  activeNumber: number
  setActiveNumber: (number: number) => void
  warrantyChoice: {
    type: WarrantyTypes
    duration: WarrantyDurationTypes
  }
  setWarrantyChoice: (warrantyChoice: {
    type: WarrantyTypes
    duration: WarrantyDurationTypes
  }) => void
  setShowContact: (showContact: boolean) => void
  initialEntry: number
  setInitialEntry: (initialEntry: number) => void
  car: Car
  setOpen: (val: boolean) => void
  hasPackOptima: boolean
  maxPackGamaEntry: number
}

export default function FinanciacionLeft({
  activeNumber,
  setActiveNumber,
  warrantyChoice,
  setWarrantyChoice,
  setShowContact,
  initialEntry,
  setInitialEntry,
  car,
  setOpen,
  hasPackOptima,
  maxPackGamaEntry,
}: Props) {
  const financedPrice = calculateDiscountedPrice(
    car.minFinancedAmount ?? 0,
    car.discount,
  )
  const applicableTIN = getApplicableTIN(
    hasPackOptima,
    car.annualInterestRate ?? 0,
    car.annualInterestRatePackGo,
  )

  const {
    capitalToFinance,
    quota: monthlyQuota,
    commissionAmount,
    insuranceAmount,
  } = calculateFinancedAmount({
    price: financedPrice ?? 0,
    warrantyData: warrantyChoice,
    initialEntry: initialEntry,
    months: activeNumber,
    TIN: applicableTIN,
    hasPackOptima: hasPackOptima,
  })

  return (
    <div
      className={`
        flex-grow pt-[31px]

        xl:pl-[100px]
      `}
    >
      <p
        className={`
          max-w-[650px] text-h5-bold text-neutral-900

          xl:text-h3
        `}
      >
        Confirma tu reserva
      </p>
      <p className="mt-4 max-w-[650px] text-base-medium text-neutral-500">
        Elige la cuota que mejor se ajuste a tu situación.
      </p>
      <Spacer size="28px" sizeMd="24px" />
      <CardOptions
        activeNumber={activeNumber}
        setActiveNumber={setActiveNumber}
        warrantyChoice={warrantyChoice}
        setWarrantyChoice={setWarrantyChoice}
        initialEntry={initialEntry}
        setInitialEntry={setInitialEntry}
        car={car as any}
        hasPackOptima={hasPackOptima}
        maxPackGamaEntry={maxPackGamaEntry}
      />
      <Spacer size="16px" sizeMd="24px" />

      <p className="mt-4 max-w-[650px] text-subtitle2-medium text-neutral-500">
        Anticipo inicial {initialEntry}€. Plazo de {activeNumber} meses.
        Mensualidad {formatPrice(Math.round(monthlyQuota))}. Interés Nominal
        (TIN) {applicableTIN.toFixed(2).replace('.', ',')}%. Comisión de
        apertura del {String(openingCommission).replace('.', ',')}%:{' '}
        {formatPrice(Math.round(commissionAmount))}. PPP (seguro de vida y
        seguro de invalidez) + Vehículo de sustitución + Retirada de carnet:{' '}
        {formatPrice(Math.round(insuranceAmount))}. Importe total a financiar{' '}
        {formatPrice(Math.round(capitalToFinance))}, con{' '}
        {warrantyChoice.duration}{' '}
        {warrantyChoice.duration === 1 ? 'año' : 'años'} de{' '}
        <span className="font-semibold">
          {WarrantyTranslationEnum[warrantyChoice.type]}
        </span>{' '}
        incluido. Las mensualidades contemplan gastos de apertura, vehículo de
        sustitución y seguro de vida, invalidez y retirada de carnet.
        Amortización permitida desde el mes 18 con una penalización del 1% sobre
        el monto amortizado. 0,5% de penalización para cancelaciones realizadas
        en el último año del préstamo.
      </p>
      <Spacer size="24px" sizeMd="36px" />

      <Button
        variant={'primary'}
        className={`h-12 max-w-[110px] text-base-bold text-white`}
        onClick={() => setShowContact(true)}
      >
        Siguiente
      </Button>
      <Spacer size="24px" sizeMd="40px" />
      <p className="mt-4 text-base-medium text-neutral-500">
        ¿Tienes duda sobre como funciona la garantía?{' '}
        <button
          className="text-primary-600 underline"
          onClick={() => {
            console.log('setOpen(true)')
            setOpen(true)
          }}
        >
          Saber más
        </button>
      </p>
    </div>
  )
}
